import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormControl, FormsModule } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, startWith } from 'rxjs/operators';
import {
    SharedModule,
    FormInputComponent,
    InputComponentConfig,
    DataService,
    ItemOf,
} from '@vendure/admin-ui/core';
import { GetCustomersListDocument, GetCustomersListQuery, LogicalOperator } from '../generated-types';
import { NgSelectModule } from '@ng-select/ng-select';

@Component({
    selector: 'vdr-customer-selector-form-input',
    templateUrl: './customer-selector.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [FormsModule, NgSelectModule, SharedModule],
})
export class CustomerSelectorComponent implements FormInputComponent, OnInit {
    searchInput$ = new Subject<string>();
    customers$: Observable<GetCustomersListQuery['customers']['items']>;
    selectedCustomer: ItemOf<GetCustomersListQuery, 'customers'> | null = null;
    isListInput?: boolean | undefined;
    config: InputComponentConfig;
    readonly: boolean;
    formControl: FormControl;

    constructor(
        private dataService: DataService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        const customerId = this.formControl.value;
        this.customers$ = this.searchInput$.pipe(
            startWith(''),
            debounceTime(300),
            distinctUntilChanged(),
            switchMap((term: any) => {
                return this.dataService
                    .query(GetCustomersListDocument, {
                        options: {
                            filter: {
                                id: {
                                    in: customerId ? [customerId.toString()] : undefined,
                                },
                                emailAddress: { contains: term.term },
                            },
                            filterOperator: LogicalOperator.OR,
                            take: 100,
                        },
                    })
                    .mapStream(data => data.customers.items);
            }),
        );
        if (customerId) {
            this.customers$.subscribe(customers => {
                this.selectedCustomer =
                    customers.find(c => c.id.toString() === customerId.toString()) ?? null;
                this.cdr.markForCheck();
            });
        }
    }

    selectCustomer(customer: ItemOf<GetCustomersListQuery, 'customers'>) {
        this.selectedCustomer = customer;
        this.formControl.setValue(customer?.id ?? null);
    }

    trackById(index: number, item: ItemOf<GetCustomersListQuery, 'customers'>): string {
        return item ? item.id : '';
    }

    compareWith<T extends ItemOf<GetCustomersListQuery, 'customers'> | string>(o1: T, o2: T) {
        const id1 = typeof o1 === 'string' ? o1 : o1.id;
        const id2 = typeof o2 === 'string' ? o2 : o2.id;
        return id1 === id2;
    }
}
